import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { TextInput, PasswordInput, Button, Stack, Alert, Text, Anchor, Group } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle } from '@tabler/icons-react';
import { useVendorAuth } from './VendorAuthContext';
import { vendorsApi } from '../../../api/vendors';
import { VendorAuthLayout } from './VendorAuthLayout';
import { BRAND_MEDIUM } from '../../../theme/brand';

export function VendorLogin() {
  const navigate = useNavigate();
  const { login } = useVendorAuth();
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null);

  const form = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: {
      email: (value) => (!value ? 'Email is required' : !/^\S+@\S+$/.test(value) ? 'Invalid email' : null),
      password: (value) => (!value ? 'Password is required' : null),
    },
  });

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true);
    setError(null);
    setEmailNotVerified(false);
    setUnverifiedEmail(null);

    try {
      await login(values.email, values.password);

      notifications.show({
        title: 'Login Successful',
        message: 'Welcome back!',
        color: 'green',
      });

      navigate('/vendor/dashboard');
    } catch (err: any) {
      const errorMessage = err.message || 'Login failed. Please check your credentials.';
      setError(errorMessage);
      
      // Check if error is due to unverified email
      // Check both err.response and err.response?.data for the emailNotVerified flag
      if (err.response?.data?.emailNotVerified || err.response?.emailNotVerified) {
        setEmailNotVerified(true);
        setUnverifiedEmail(err.response?.data?.email || err.response?.email || values.email);
      }

    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!unverifiedEmail) return;

    setResending(true);
    try {
      await vendorsApi.resendVerification(unverifiedEmail);
      notifications.show({
        title: 'Verification Email Sent',
        message: 'Please check your inbox for the verification link.',
        color: 'green',
      });
      setEmailNotVerified(false);
    } catch (err: any) {
      notifications.show({
        title: 'Failed to Resend',
        message: err.response?.data?.error || 'Failed to resend verification email. Please try again.',
        color: 'red',
      });
    } finally {
      setResending(false);
    }
  };

  return (
    <VendorAuthLayout title="Vendor sign in" subtitle="Sign in to manage your company profile and bids.">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md" role="alert">
          <Text size="sm">{error}</Text>
          {emailNotVerified && unverifiedEmail && (
            <Text size="sm" mt="xs">
              Didn't receive the verification email?{' '}
              <Anchor
                component="button"
                type="button"
                size="sm"
                fw={500}
                td="underline"
                onClick={handleResendVerification}
                disabled={resending}
              >
                {resending ? 'Sending…' : 'Resend verification email'}
              </Anchor>
            </Text>
          )}
        </Alert>
      )}

      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="you@company.com"
            type="email"
            autoComplete="username"
            required
            size="md"
            radius="md"
            {...form.getInputProps('email')}
          />

          <Stack gap={6}>
            <PasswordInput
              label="Password"
              placeholder="Enter your password"
              autoComplete="current-password"
              required
              size="md"
              radius="md"
              {...form.getInputProps('password')}
            />
            <Group justify="flex-end">
              <Anchor component={Link} to="/vendor/forgot-password" size="sm" c={BRAND_MEDIUM}>
                Forgot password?
              </Anchor>
            </Group>
          </Stack>

          <Button type="submit" loading={loading} fullWidth color={BRAND_MEDIUM} size="md" radius="md">
            Sign in
          </Button>
        </Stack>
      </form>

      <Text size="sm" ta="center" c="dimmed">
        New supplier?{' '}
        <Anchor component={Link} to="/vendor/register" fw={600} c={BRAND_MEDIUM}>
          Register your company
        </Anchor>
      </Text>
    </VendorAuthLayout>
  );
}
